import React, { useCallback, useState, useEffect } from 'react';
import { Collapse, Switch } from 'antd';
import { MinusCircleFilled, PlusCircleFilled } from '@ant-design/icons';
import './styles.scss';

const { Panel } = Collapse;

const MyCollapse = ({
  children,
  header,
  defaultActiveKey,
  activeKey,
  onChange,
  bordered = false,
  expandIconPosition = 'left',
  expandIcon,
  className = '',
  disabled = false,
  // Switch props
  switchValue = false,
  onSwitchChange,
  showSwitch = false,
  switchName,
  ...restProps
}) => {
  const [activeKeys, setActiveKeys] = useState(
    activeKey || defaultActiveKey || []
  );
  const [internalSwitchValue, setInternalSwitchValue] = useState(switchValue);

  // Update internal switch value when prop changes
  useEffect(() => {
    setInternalSwitchValue(switchValue);
  }, [switchValue]);

  // Reset switch when collapse closes
  useEffect(() => {
    const isCollapsed = !activeKeys || activeKeys.length === 0;
    if (isCollapsed && internalSwitchValue) {
      const newSwitchValue = false;
      setInternalSwitchValue(newSwitchValue);
      if (onSwitchChange) {
        onSwitchChange(switchName, newSwitchValue);
      }
    }
  }, [activeKeys, internalSwitchValue, onSwitchChange]);

  // Auto switch behavior: ON when open, OFF when closed
  useEffect(() => {
    const isOpen = activeKeys && activeKeys.length > 0;
    const newSwitchValue = isOpen;

    if (internalSwitchValue !== newSwitchValue) {
      setInternalSwitchValue(newSwitchValue);
      if (onSwitchChange) {
        onSwitchChange(switchName, newSwitchValue);
      }
    }
  }, [activeKeys, internalSwitchValue, onSwitchChange]);

  const _renderExpandIcon = useCallback(
    ({ isActive }) => {
      // If custom expandIcon is provided, use it
      if (expandIcon) {
        return typeof expandIcon === 'function'
          ? expandIcon({ isActive })
          : expandIcon;
      }

      // Default icons
      return isActive ? (
        <MinusCircleFilled className="my-icon-md icon-expand" />
      ) : (
        <PlusCircleFilled className="my-icon-md icon-expand" />
      );
    },
    [expandIcon]
  );

  const _handleChange = useCallback(
    (keys) => {
      setActiveKeys(keys);
      if (onChange) {
        onChange(keys);
      }
    },
    [onChange]
  );

  const _handleSwitchChange = useCallback(
    (checked) => {
      setInternalSwitchValue(checked);
      if (onSwitchChange) {
        onSwitchChange(checked);
      }
    },
    [onSwitchChange]
  );

  const _renderHeader = useCallback(() => {
    if (showSwitch) {
      return (
        <div className="my-collapse-header-with-switch">
          <div className="header-content">{header}</div>
          <div className="header-switch">
            <Switch
              checked={internalSwitchValue}
              onChange={_handleSwitchChange}
              size="small"
            />
          </div>
        </div>
      );
    }
    return header;
  }, [header, showSwitch, internalSwitchValue, _handleSwitchChange]);

  const disabledClass = disabled ? 'my-collapse-disabled' : '';

  return (
    <div id="my-collapse" className={`${className} ${disabledClass}`}>
      <Collapse
        activeKey={activeKey || activeKeys}
        onChange={_handleChange}
        bordered={bordered}
        expandIcon={_renderExpandIcon}
        expandIconPosition={expandIconPosition}
        {...restProps}
      >
        <Panel header={_renderHeader()} key="1">
          <div className="my-collapse-content">{children}</div>
        </Panel>
      </Collapse>
    </div>
  );
};

export default MyCollapse;
